<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.folder-open-regular"></svg-vue>
          <h1 class="content-title">
            {{ $t('Repairable Defects') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app rounded-none rounded-l-md" to="/admin/repairable-defects/new">
            {{ $t('Create') }}
          </router-link>
          <button @click.prevent="$router.back()" type="button" class="btn btn-app rounded-none rounded-r-md">
            {{ $t('Back') }}
          </button>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <div class="px-4 py-3 md:w-2/3 items-center md:flex md:justify-between sm:px-6">
          <!-- component -->
          <input type="text" id="search" v-model.lazy="filters.search" :placeholder="$t('Search')" @change="getDefects" class="form-input rounded-none rounded-l-md" />
          <div class="flex relative w-full">
            <input-select
              :options="sortOptions"
              @change="changeSort"
              class="w-full"
              required
              classes="form-input rounded-none"
              id="sorting"
              option-label="value"
              v-model="sort.column"
            ></input-select>
          </div>
          <button @click="changeSort" class="flex text-gray-800 rounded-none rounded-r-md font-bold py-2 border border-gray-300 px-6" type="button">
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-down-alt-regular" v-show="sort.order === 'asc'"></svg-vue>
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-up-alt-regular" v-show="sort.order === 'desc'"></svg-vue>
            <span class="ml-2">{{ $t('Sort') }}</span>
          </button>
        </div>
        <div class="border-t border-gray-200">
          <template v-if="repairableDefectList.length > 0">
            <ul>
              <template v-for="(defect, index) in repairableDefectList">
                <li :class="{ 'border-t border-gray-200': index !== 0 }" :key="index">
                  <router-link
                    :to="'/admin/repairable-defects/' + defect.id + '/edit'"
                    class="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out"
                  >
                    <div class="flex items-center px-4 py-4 sm:px-6">
                      <div class="min-w-0 flex-1 flex items-center">
                        <div class="min-w-0 flex-1 r-4 lg:grid lg:grid-cols-2 lg:gap-4">
                          <div>
                            <div class="text-sm font-medium leading-5 text-gray-800 truncate">
                              {{ defect.title }}
                            </div>
                            <div class="mt-2 text-sm leading-5 text-blue-400 bg-gray-100 px-3 py-2 hover:bg-white">
                              <router-link :to="`/admin/repairable-devices/${defect.device.id}/edit`" class="flex items-center">
                                <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4" icon="font-awesome.paperclip-solid"></svg-vue>
                                {{ defect.device.name }}
                                <span class="text-sm text-gray-700 px-2">[{{ defect.device.model }}]</span>
                              </router-link>
                            </div>
                          </div>
                          <div class="hidden lg:block">
                            <div class="flex justify-start">
                              <div class="mx-4">
                                <div class="text-sm font-medium text-gray-800 truncate">
                                  {{ $t('Required Time') }}:
                                  {{ defect.required_time }}
                                </div>
                                <div class="text-sm truncate">
                                  {{ $t('Cost') }}: {{ defect.cost | currency }},{{ $t('Price') }}:
                                  {{ defect.price | currency }}
                                </div>
                                <div class="text-sm text-gray-700 truncate">{{ $t('Added') }}: {{ defect.created_at }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.angle-right-regular"></svg-vue>
                      </div>
                    </div>
                  </router-link>
                </li>
              </template>
            </ul>
          </template>
          <template v-else="!loading">
            <no-content />
          </template>
        </div>
        <nav class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div class="hidden sm:block">
            <p class="text-sm leading-5 text-gray-700">
              {{ $t('Showing') }}
              <span class="font-medium">{{ pagination.perPage * pagination.currentPage - pagination.perPage + 1 }}</span>
              {{ $t('to') }}
              <span class="font-medium">{{
                pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total
              }}</span>
              {{ $t('of') }}
              <span class="font-medium">{{ pagination.total }}</span>
              {{ $t('results') }}
            </p>
          </div>
          <div class="flex-1 flex justify-between sm:justify-end">
            <button
              :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage <= 1"
              @click="changePage(pagination.currentPage - 1)"
              class="pagination-link"
              type="button"
            >
              {{ $t('Previous') }}
            </button>
            <button
              :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage >= pagination.totalPages"
              @click="changePage(pagination.currentPage + 1)"
              class="ml-3 pagination-link"
              type="button"
            >
              {{ $t('Next') }}
            </button>
          </div>
        </nav>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'list',
  metaInfo() {
    return {
      title: this.$i18n.t('Repairable Defects'),
    };
  },
  data() {
    return {
      page: 1,
      filters: {
        search: null,
      },
      sort: {
        order: 'asc',
        column: 'created_at',
      },
      perPage: 10,
      loading: true,
      sortOptions: [
        { id: 'title', value: this.$i18n.t('Title') },
        { id: 'price', value: this.$i18n.t('Price') },
        { id: 'cost', value: this.$i18n.t('Cost') },
        { id: 'created_at', value: this.$i18n.t('Created at') },
      ],
      pagination: {
        currentPage: 0,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },
      repairableDefectList: [],
    };
  },
  mounted() {
    this.getDefects();
  },
  methods: {
    changePage(page) {
      if (page > 0 && page <= this.pagination.totalPages && page !== this.page) {
        this.page = page;
        this.getDefects();
      }
    },
    changeSort() {
      if (this.sort.order === 'asc') {
        this.sort.order = 'desc';
      } else if (this.sort.order === 'desc') {
        this.sort.order = 'asc';
      }
      this.getDefects();
    },
    getDefects() {
      this.loading = true;
      axios
        .get(route('defects.index'), {
          params: {
            page: this.page,
            sort: this.sort,
            perPage: this.perPage,
            search: this.filters.search,
          },
        })
        .then((response) => {
          this.repairableDefectList = response.data.items;
          this.pagination = response.data.pagination;
          if (this.pagination.totalPages < this.pagination.currentPage) {
            this.page = this.pagination.totalPages;
            this.getDefects();
          } else {
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
